import {tokenStore, assertSessionCurrent} from './tokenStore.ts';
import {withSessionLock} from './sessionLock.ts';
export {tokenStore, assertSessionCurrent} from './tokenStore.ts';

/**
 * 账户 API 客户端:与现有官网同一套账号服务(medge-ai-api)。
 * 令牌持久化与跨标签页同步由 tokenStore 管理；存储不可用时保留内存会话。
 */
/**
 * 开发与本地预览走同源 /v1(vite 代理到账号服务);构建产物用构建期注入的地址,
 * 测试环境必须指向测试账号服务,不能连生产。CI 在 VITE_API_BASE_URL 里注入。
 */
const buildEnv = (import.meta as { env?: Record<string, string | undefined> & { DEV?: boolean } }).env ?? {};
export const API_BASE_URL = buildEnv.VITE_API_BASE_URL?.replace(/\/+$/, '') ?? '';

export interface ApiUser {
  id: string;
  phone?: string;
  email?: string;
  nick_name?: string;
  avatar_url?: string;
  status: 'active' | 'disabled';
  has_password: boolean;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  is_new: boolean;
  user: ApiUser;
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
}

export interface OkResponse {
  ok: boolean;
}

/** 后端统一错误体 {code, msg}:业务码与 HTTP status 解耦,页面两者共同判断。 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: number;
  readonly msg: string;
  /** 仅 429 等携带 Retry-After 时存在,单位秒。 */
  readonly retryAfterSeconds?: number;

  constructor(status: number, code: number, msg: string, retryAfterSeconds?: number) {
    super(msg || `API error ${status}/${code}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.msg = msg;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** fetch 本身失败(断网、DNS、CORS 拦截),没有 HTTP 响应。 */
export class NetworkError extends Error {
  constructor(message = '网络连接失败，请检查网络后重试') {
    super(message);
    this.name = 'NetworkError';
  }
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Retry-After 支持秒数或 HTTP-date 两种格式。 */
function parseRetryAfter(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const seconds = Number(raw);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
  const date = Date.parse(raw);
  if (Number.isNaN(date)) return undefined;
  const diff = Math.ceil((date - Date.now()) / 1000);
  return diff > 0 ? diff : 0;
}

type SessionExpiredListener = () => void;
let sessionExpiredListener: SessionExpiredListener | null = null;

/** 会话模块注册:刷新失败或重放仍 401 时被调用,负责清 UI 登录态。 */
export function setOnSessionExpired(listener: SessionExpiredListener | null): void {
  sessionExpiredListener = listener;
}

function expireSession(expectedVersion: number): void {
  assertSessionCurrent(expectedVersion);
  tokenStore.clear();
  sessionExpiredListener?.();
}

let refreshRequest: { version: number; promise: Promise<void> } | null = null;
const credentialWork=new Map<number,Promise<void>>();

/** Approval proves ownership with the active refresh token. Serialize that proof
 * with rotation, both in this tab and through the existing cross-tab lease. */
async function withCredentialLock<T>(signal:AbortSignal,version:number,work:(assertHeld:()=>void)=>Promise<T>):Promise<T> {
  const previous=credentialWork.get(version)??Promise.resolve();
  let release!:()=>void;
  const held=new Promise<void>(resolve=>{release=resolve});
  const tail=previous.then(()=>held);
  credentialWork.set(version,tail);
  try {
    await previous;signal.throwIfAborted();
    return tokenStore.isShared()?await withSessionLock(signal,work):await work(()=>signal.throwIfAborted());
  } finally {release();if(credentialWork.get(version)===tail)credentialWork.delete(version)}
}

/** 合并并发刷新:同一时刻只有一个真正的刷新请求在飞。 */
export function refreshSession(version = tokenStore.getSessionVersion()): Promise<void> {
  assertSessionCurrent(version);
  if (!refreshRequest || refreshRequest.version !== version) {
    const observedRefresh = tokenStore.getRefreshToken();
    const request = { version, promise: Promise.resolve() };
    request.promise = withDeadline(undefined, DEFAULT_TIMEOUT_MS, async signal => {
      const renew = async (assertHeld: () => void) => {
        assertHeld();
        assertSessionCurrent(version);
        tokenStore.persistLegacy(version);
        // Another tab may have completed the same renewal while we waited.
        if (tokenStore.getRefreshToken() !== observedRefresh) return;
        await doRefresh(version, signal, assertHeld);
      };
      await withCredentialLock(signal, version, renew);
    })
      .finally(() => { if (refreshRequest === request) refreshRequest = null });
    refreshRequest = request;
  }
  return refreshRequest.promise;
}

async function doRefresh(version: number, signal: AbortSignal, assertHeld: () => void): Promise<void> {
  assertSessionCurrent(version);
  const refresh = tokenStore.getRefreshToken();
  if (!refresh) {
    expireSession(version);
    throw new ApiError(401, 4001, '登录已过期');
  }
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/v1/auth/token/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
      signal,
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new NetworkError();
  }
  assertHeld();
  assertSessionCurrent(version);
  if (!response.ok) {
    const error = await parseErrorResponse(response);
    assertHeld();
    assertSessionCurrent(version);
    if (tokenStore.getRefreshToken() !== refresh) return;
    // 只有凭证被明确拒绝才退出。限流、网关故障等保留会话并返回真实错误。
    if (response.status === 401 || (response.status === 403 && error.code === 4003)) expireSession(version);
    throw error;
  }
  let data: RefreshResponse;
  try {
    data = (await response.json()) as RefreshResponse;
  } catch {
    throw new NetworkError('登录服务响应异常，请稍后重试');
  }
  if (!data || typeof data.access_token !== 'string' || !data.access_token.trim() || typeof data.refresh_token !== 'string' || !data.refresh_token.trim()) {
    throw new NetworkError('登录服务响应异常，请稍后重试');
  }
  assertHeld();
  assertSessionCurrent(version);
  if (tokenStore.getRefreshToken() !== refresh) return;
  tokenStore.rotateTokens(data.access_token, data.refresh_token, version);
}

const DEFAULT_TIMEOUT_MS = 12_000;

/** 包括响应正文和刷新重放在内的总时限；关闭弹窗时可单独取消。 */
async function withDeadline<T>(callerSignal: AbortSignal | undefined, timeoutMs: number, operation: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  const cancel = () => controller.abort(callerSignal?.reason);
  if (callerSignal?.aborted) cancel();
  else callerSignal?.addEventListener('abort', cancel, { once: true });
  let rejectAbort: (error: unknown) => void;
  const aborted = new Promise<never>((_, reject) => { rejectAbort = reject });
  const onAbort = () => rejectAbort(controller.signal.reason);
  controller.signal.addEventListener('abort', onAbort, { once: true });
  const timer = setTimeout(() => controller.abort(new NetworkError('请求响应超时，请稍后重试')), timeoutMs);
  try {
    controller.signal.throwIfAborted();
    return await Promise.race([operation(controller.signal), aborted]);
  } finally {
    clearTimeout(timer);
    callerSignal?.removeEventListener('abort', cancel);
    controller.signal.removeEventListener('abort', onAbort);
  }
}

async function parseErrorResponse(response: Response): Promise<ApiError> {
  let code = 0;
  let msg = '';
  try {
    const data = (await response.json()) as { code?: number; msg?: string };
    if (typeof data.code === 'number') code = data.code;
    if (typeof data.msg === 'string') msg = data.msg;
  } catch {
    // 非 JSON 错误体(如网关 502 页面):保留 status,code=0
  }
  return new ApiError(response.status, code, msg, parseRetryAfter(response.headers.get('Retry-After')));
}

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** true 时附加 Authorization 头并启用 401 刷新重放;公开接口必须为 false。 */
  auth?: boolean;
  /** Fence a user confirmation against a different account installed by another tab. */
  expectedSessionVersion?: number;
  /** Attach the current refresh proof while holding the same lock as rotation.
   * Read it anew on an authenticated retry, never copy it into a caller's URL. */
  refreshProof?: boolean;
  /** 幂等键:同一次兑换重试不会重复加研值。 */
  idempotencyKey?: string;
  /** 协议定义的专用请求头(如微信事务查询凭证)。 */
  headers?: Record<string, string>;
  signal?: AbortSignal;
  timeoutMs?: number;
}

export function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const version = options.auth ? options.expectedSessionVersion ?? tokenStore.getSessionVersion() : undefined;
  return withDeadline(options.signal, options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    signal => doFetch<T>(path, { ...options, signal }, true, version));
}

async function doFetch<T>(path: string, options: ApiRequestOptions, allowRefreshRetry: boolean, version: number | undefined): Promise<T> {
  options.signal?.throwIfAborted();
  if (version !== undefined) assertSessionCurrent(version);
  const send=async(assertHeld:()=>void)=>{
    assertHeld();
    if(version!==undefined)assertSessionCurrent(version);
    const headers:Record<string,string>={...options.headers};
    const body=options.refreshProof?{...(options.body as Record<string,unknown>|undefined),refresh:tokenStore.getRefreshToken()}:options.body;
    if(body!==undefined)headers['Content-Type']='application/json';
    if(options.auth){const token=tokenStore.getAccessToken();if(token)headers.Authorization=`Bearer ${token}`}
    if(options.idempotencyKey)headers['Idempotency-Key']=options.idempotencyKey;
    let response:Response;
    try {
      response=await fetch(`${API_BASE_URL}${path}`,{method:options.method??(body!==undefined?'POST':'GET'),headers,body:body!==undefined?JSON.stringify(body):undefined,signal:options.signal});
    }catch(error){if(isAbortError(error))throw error;throw new NetworkError()}
    assertHeld();
    return {response,authorization:headers.Authorization};
  };
  const {response,authorization}=options.refreshProof
    ?await withCredentialLock(options.signal!,version!,send)
    :await send(()=>options.signal?.throwIfAborted());

  options.signal?.throwIfAborted();
  if (version !== undefined) assertSessionCurrent(version);

  if (response.status === 401 && version !== undefined) {
    if (allowRefreshRetry) {
      // 迟到的旧 access 401 可能发生在其他请求已完成刷新之后。
      if (authorization === `Bearer ${tokenStore.getAccessToken()}`) await refreshSession(version);
      // 任何请求最多因刷新重放一次,防止循环
      return doFetch<T>(path, options, false, version);
    }
    // 刷新成功但重放仍 401:登录态已不可恢复
    const error = await parseErrorResponse(response);
    options.signal?.throwIfAborted();
    expireSession(version);
    throw error;
  }

  if (!response.ok) {
    const error = await parseErrorResponse(response);
    options.signal?.throwIfAborted();
    if (version !== undefined) assertSessionCurrent(version);
    throw error;
  }
  if (response.status === 204) return undefined as T;
  const data = (await response.json()) as T;
  options.signal?.throwIfAborted();
  if (version !== undefined) assertSessionCurrent(version);
  return data;
}

/** 登录、发码错误 → 稳定文案;不透传后端原始响应。凭证类错误按当前操作措辞。 */
export function describeAuthError(error: unknown, action: 'code' | 'password' = 'code'): { message: string; retryAfterSeconds?: number } {
  if (error instanceof NetworkError) return { message: error.message };
  if (!(error instanceof ApiError)) return { message: '系统异常，请稍后重试' };
  if (error.status === 429) {
    return { message: '操作过于频繁，请稍后重试', ...(error.retryAfterSeconds ? { retryAfterSeconds: error.retryAfterSeconds } : {}) };
  }
  switch (error.code) {
    case 1002: return { message: '请输入正确的中国大陆手机号' };
    case 1001:
    case 1003: return { message: action === 'password' ? '手机号或密码格式不正确' : '手机号或验证码格式不正确' };
    case 4001: return { message: action === 'password' ? '手机号或密码不正确，请重试' : '验证码错误或已失效，请重新获取' };
    case 4003: return { message: '该账号已被禁用，请联系客服' };
    case 9002: return { message: '服务暂时不可用，请稍后重试' };
    default: return { message: '系统异常，请稍后重试' };
  }
}
